import type { FloorInfo, PoiContent } from '@/services/dto';
import { useConfigStore } from '@/stores';
import { buildPoiContentSlide } from '@/utils/floor-guide/buildPoiContentSlide';
import type { IFloor } from 'dabeeomaps/dist/src/indexAPI';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePoiDetail } from './usePoiDetail';

export const usePoiContentModalState = (
    selectedPoiId?: string,
    selectedFloor?: IDataFloor | IFloor | null | FloorInfo,
    mapPoiDisplayName?: string,
    selectedContentId?: string
) => {
    const { handleGetPoiItemInAllBuildings, handleGetPoiListInAllBuildings } = usePoiDetail();

    const language = useConfigStore((state) => state.config?.ui?.language);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const storeContentsPropertyManagement = useConfigStore((state) => state.config?.server?.store?.contentsPropertyManagement);

    const [selectedPoiContent, setSelectedPoiContent] = useState<PoiContent | undefined>(() => handleGetPoiItemInAllBuildings(selectedPoiId));

    const relatedPoiContentList = useMemo(
        () => handleGetPoiListInAllBuildings(selectedPoiId),
        [selectedPoiId, handleGetPoiListInAllBuildings]
    );

    const selectedBuildingName = useMemo(() => {
        if (!selectedPoiContent || !Array.isArray(buildings)) {
            return '';
        }
        const building = buildings.find(
            (building) => building.id === selectedPoiContent.studioBuildingId
        );
        return (building?.name?.[language] ? building?.name?.[language] : building?.name?.['ko']) || '';
    }, [selectedPoiContent, buildings, language]);

    const floorName = useMemo(() => {
        const name = selectedFloor?.name;

        if (!Array.isArray(name) || !name?.length) {
            return '';
        }

        return name.find((item) => item.lang === language)?.text || name.find((item) => item.lang === 'ko')?.text || '';
    }, [selectedFloor, language]);

    const poiContentSlide = useMemo(
        () =>
            buildPoiContentSlide(
                selectedPoiContent,
                language,
                buildings,
                floorName,
                selectedBuildingName,
                selectedPoiId,
                mapPoiDisplayName,
                storeContentsPropertyManagement
            ),
        [
            selectedPoiContent,
            language,
            buildings,
            floorName,
            selectedBuildingName,
            selectedPoiId,
            mapPoiDisplayName,
            storeContentsPropertyManagement,
        ]
    );

    const resolveSelectedPoiContent = useCallback((): PoiContent | undefined => {
        const relatedList = handleGetPoiListInAllBuildings(selectedPoiId);
        if (selectedContentId && relatedList.length) {
            const matched = relatedList.find((poi) => poi.id === selectedContentId);
            if (matched) {
                return matched;
            }
        }
        if (relatedList.length > 0) {
            return relatedList[0];
        }
        return handleGetPoiItemInAllBuildings(selectedPoiId);
    }, [selectedPoiId, selectedContentId, handleGetPoiListInAllBuildings, handleGetPoiItemInAllBuildings]);

    useEffect(() => {
        setSelectedPoiContent(resolveSelectedPoiContent());
    }, [resolveSelectedPoiContent]);

    return {
        poiContentSlide,
        relatedPoiContentList,
        selectedPoiContentId: selectedPoiContent?.id,
        setSelectedPoiContent,
    };
};
